import debounce from "lodash/debounce"
import {tableModules} from '~/utils/tableModules'

interface RouteParamMapping {
    param: string;
    prop: string;
}

interface ListingState {
    search: string
    orderBy: string
    orderDirection: 'asc' | 'desc'
    perPage: number
    perPageOptions: number[]
    columns: number
    page: number
    lastPage: number
    total: number
    custom: Record<string, any>
    filter: FilterDefinition[]
    links: any[]
}

type ToolPostData = {
    rawResponse?: boolean
    [key: string]: any
}

export const useTableStore = () => {
    const name = ref<string | null>(null)
    const route = ref<string | null>(null)
    const routeParams = ref<RouteParamMapping[]>([])
    const defaultOrder = ref<{ column: string; direction: 'asc' | 'desc' } | null>(null)
    const custom = ref<any | null>(null)
    // page => row collection
    const data = ref<Record<number, any[]>>({}) // store previously fetched data per page
    const responseData = ref<any>({})
    const columns = ref<Array<ColumnDefinition> | null>(null)
    const filters = ref<Array<FilterDefinition> | null>(null)
    const quickFilters = ref<Array<QuickFilterDefinition> | null>(null)
    const tools = ref<Array<ToolDefinition> | null>(null)
    const primaryKey = ref<string>('id')

    const activeTool = ref<string | null>(null)
    const loading = ref<boolean>(false)
    const inverseSelection = ref<boolean>(false)
    const selectedRows = ref<Array<string | number>>([])
    const filterOptionCache = ref<any[]>([])

    const listing = ref<ListingState>({
        search: "",
        orderBy: "id",
        orderDirection: "asc",
        perPage: 10,
        perPageOptions: [10, 50, 100],
        columns: 6,
        page: 1,
        lastPage: 1,
        total: 0,
        custom: {},
        filter: [],
        links: []
    })

    const rows = computed(() => {
        return data.value[listing.value.page] || []
    })

    const selectedCount = computed(() => {
        return inverseSelection.value ? listing.value.total - selectedRows.value.length : selectedRows.value.length
    })

    function resetSelection() {
        inverseSelection.value = false
        selectedRows.value = []
    }

    function updateOrderBy(column: string) {
        if (listing.value.orderBy === column) {
            if (listing.value.orderDirection === 'asc') {
                listing.value.orderDirection = 'desc'
            } else if (listing.value.orderBy === defaultOrder.value?.column && defaultOrder.value?.direction === 'desc') {
                listing.value.orderDirection = 'asc'
            } else {
                listing.value.orderBy = defaultOrder.value?.column ?? 'id'
                listing.value.orderDirection = defaultOrder.value?.direction ?? 'asc'
            }
        } else {
            listing.value.orderBy = column
            listing.value.orderDirection = 'asc'
        }
        fetchData(true)
    }

    async function preloadPages() {
        const {$apiRoute} = useNuxtApp()
        for (let i = -1; i <= 2; i++) {
            const pageToLoad = listing.value.page + i
            if (pageToLoad < 1 || pageToLoad > listing.value.lastPage || data.value[pageToLoad]) continue
            try {
                const params: Record<string, any> = {}
                routeParams.value.forEach((mapping) => {
                    params[mapping.param] = listing.value.custom[mapping.prop]
                })
                const response: any = await $lara.get($apiRoute(route.value + '.index', params), {
                    params: {...listing.value, page: pageToLoad}
                })
                data.value[pageToLoad] = response.data
            } catch (error) {
                console.log(error)
            }
        }
    }

    const preloadPagesDebounced = debounce(preloadPages, 1000, {maxWait: 5000})

    async function fetchData(fresh = false): Promise<void> {
        const {$apiRoute} = useNuxtApp()

        loading.value = true

        preloadPagesDebounced?.cancel()

        if (fresh || data.value[listing.value.page]) {
            data.value = {}
        }

        const params: Record<string, any> = {}
        routeParams.value.forEach((mapping) => {
            params[mapping.param] = listing.value.custom[mapping.prop]
        })

        const response = await $lara.get($apiRoute(route.value + '.index', params), {
            params: {...listing.value}
        })
        loading.value = false

        responseData.value = response

        if (response.error) return

        data.value[listing.value.page] = response.data
        listing.value.page = response.meta?.current_page ?? response.current_page
        listing.value.lastPage = response.meta?.last_page ?? response.last_page
        listing.value.total = response.meta?.total ?? response.total
        listing.value.links = response.meta?.links ?? response.links

        if (listing.value.page > listing.value.lastPage) {
            listing.value.page = listing.value.lastPage
        }

        await preloadPagesDebounced()
    }

    async function postToolData(tool: string, toolData: ToolPostData = {}) {
        const {$apiRoute} = useNuxtApp()
        const params: Record<string, any> = {}
        routeParams.value.forEach((mapping) => {
            params[mapping.param] = listing.value.custom[mapping.prop]
        })

        return $lara.post($apiRoute(route.value + '.action', params), {
            //@ts-expect-error
            rawResponse: toolData.rawResponse ?? false,
            body: {
                ...listing.value,
                ...listing.value.custom,
                selection: {
                    inverseSelection: inverseSelection.value ?? false,
                    selectedRows: selectedRows.value ?? []
                },
                tool,
                toolData
            }
        })
    }

    function initialize(module: string) {
        const moduleDefinition = tableModules[module]
        if (!moduleDefinition) {
            throw new Error(`Module definition for '${module}' not found.`)
        }
        name.value = moduleDefinition.name
        route.value = moduleDefinition.route
        routeParams.value = (moduleDefinition.routeParams ?? []) as RouteParamMapping[]
        defaultOrder.value = moduleDefinition.defaultOrder ?? null
        custom.value = moduleDefinition.custom ?? null
        columns.value = moduleDefinition.columns
        filters.value = moduleDefinition.filters ?? null
        quickFilters.value = moduleDefinition.quickFilters ?? null
        tools.value = moduleDefinition.tools ?? null
        primaryKey.value = moduleDefinition.primaryKey ?? 'id'

        if (defaultOrder.value) {
            listing.value.orderBy = defaultOrder.value.column
            listing.value.orderDirection = defaultOrder.value.direction
        }

        // Load filters from local storage
        const storedFilters = localStorage.getItem(`filters-${name.value}`)
        if (storedFilters !== null && storedFilters.length > 0) {
            listing.value.filter = JSON.parse(storedFilters)
        } else if (storedFilters === null) {
            // Apply default quick filters if no filters are stored
            quickFilters?.value?.forEach((filter) => {
                if (filter.default) {
                    // @ts-expect-error
                    listing.value.filter.push(filter)
                }
            })
        }

        // Load orderBy and orderDirection from local storage
        const storedOrderBy = localStorage.getItem(`${name.value}-orderBy`)
        const storedOrderDirection = localStorage.getItem(`${name.value}-orderDirection`)
        if (storedOrderBy) {
            listing.value.orderBy = storedOrderBy
        }
        if (storedOrderDirection === 'asc' || storedOrderDirection === 'desc') {
            listing.value.orderDirection = storedOrderDirection
        }
    }

    // Watch filters and update local storage
    watch(() => listing.value.filter, (newFilters) => {
        if (newFilters.length > 0) {
            localStorage.setItem(`filters-${name.value}`, JSON.stringify(newFilters))
        } else {
            localStorage.setItem(`filters-${name.value}`, JSON.stringify([]))
        }
    }, {deep: true})

    // Watch orderBy and update local storage
    watch(() => listing.value.orderBy, (newOrderBy) => {
        localStorage.setItem(`${name.value}-orderBy`, newOrderBy)
    })

    // Watch orderDirection and update local storage
    watch(() => listing.value.orderDirection, (newOrderDirection) => {
        localStorage.setItem(`${name.value}-orderDirection`, newOrderDirection)
    })

    const fetchDebounced = debounce(() => {
        listing.value.page = 1
        fetchData(true)
    }, 250)

    watch(() => listing.value.page, () => fetchData())
    watch(() => listing.value.perPage, () => fetchData(true))
    watch([() => listing.value.search, () => listing.value.filter], () => fetchDebounced())
    watch(() => inverseSelection.value, () => selectedRows.value = [])

    const allRowsSelected = computed({
        get: () => (inverseSelection.value && selectedRows.value.length === 0) || (!inverseSelection.value && selectedRows.value.length === listing.value.total && listing.value.total > 0),
        set: (value) => {
            inverseSelection.value = value
            selectedRows.value = []
        }
    })

    return {
        loading,
        responseData,
        columns,
        filters,
        quickFilters,
        tools,
        rows,
        listing,
        inverseSelection,
        selectedRows,
        selectedCount,
        activeTool,
        defaultOrder,
        custom,
        route,
        filterOptionCache,
        primaryKey,
        allRowsSelected,
        initialize,
        updateOrderBy,
        fetchData,
        resetSelection,
        postToolData
    }
}